import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, Platform, ToastController } from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class Helper {
  loaderObj: any;

  constructor(
    private platform: Platform,
    public router: Router,
    public toastController: ToastController,
    private loadingController: LoadingController,
  ) { }

  //Helper
  redirectPage(slug:any) {
    if (!slug) {
      return;
    }

    const target = `${slug}`.trim();
    if (!target) {
      return;
    }

    if (/^https?:\/\//i.test(target)) {
      try {
        const parsedUrl = new URL(target);
        this.router.navigateByUrl(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
        return;
      } catch {
        window.location.href = target;
        return;
      }
    }

    const normalizedTarget = target.startsWith('/') ? target : `/${target}`;
    this.router.navigateByUrl(normalizedTarget);
  }

  urlToArray(urlStr:any) {
    return urlStr.split("/");
  }

  isNumber(n:any) {
    return !isNaN(parseFloat(n)) && !isNaN(n - 0)
  }

  /* After 2sec will disappear */
  async show_message(msg: any, type: any = "success", position: any = 'top', duration: number = 2500) {
    const toast = await this.toastController.create({
      message: msg,
      color: type == 'error' ? 'danger' : type == 'success' ? 'success' : type,
      duration: duration,    
      position: position
    });
    await toast.present();
  }

  async startLoader(){
    this.loaderObj = await this.loadingController.create({
      message: 'Loading...',
      duration: 2500,
      spinner: 'circles'
    });
    await this.loaderObj.present();
  }
  
  async stopLoader(){
    if(this.loaderObj){
      await this.loaderObj.dismiss();
    }
  }

  getAuthType(){
    let authType = localStorage.getItem("authType");
    if(!authType){
      authType = 'user';
    }
    return authType;
  }
  setAuthType(authType:any='user'){
    localStorage.setItem("authType", authType);
    return authType;
  }

  //  
  getDeviceType(){
    let device_type = localStorage.getItem("deviceType");
    if(!device_type){
      device_type = this.setDeviceType();
    }
    return device_type;
  }
  setDeviceType() {
    let device_type = 'Other';
    if (this.platform.is('ios') || this.platform.is('ipad') || this.platform.is('iphone')) {
      device_type = 'ios';
    } else if (this.platform.is('android')) {
      device_type = 'android';
    } else if (this.platform.is('mobileweb')) {
      device_type = 'web';
    }

    localStorage.setItem("deviceType", device_type);
    return device_type;
  }

  //
  getDeviceToken(){
    let deviceToken = localStorage.getItem("deviceToken");
    if(!deviceToken){
      deviceToken = '-';
    }
    return deviceToken;
  }

  //user
  getUserData(){
    let userData:any;

    try {
      let userDataStr:any = localStorage.getItem("userData");
      if(userDataStr){
        userData = JSON.parse(userDataStr);
      }
    } catch (error) {
      localStorage.removeItem("userData");
      this.dbg('Invalid userData cleared from localStorage', error);
    }

    return userData ? userData : '';
  }
  setUserData(userData:any){
    if(userData){
      localStorage.setItem("userData", JSON.stringify(userData));
    }
  }
  clearUserData(){
    localStorage.removeItem("userData");
  }

  getUserToken(){
    let userToken:any = localStorage.getItem("userToken");
    return userToken ? userToken : '';
  }
  setUserToken(userToken:any){
    if(userToken){
      localStorage.setItem("userToken", userToken);
    }
  }
  clearUserToken(){
    localStorage.removeItem("userToken");
  }

  /*  */
  checkGenerateJwtToken() {
    const userData = this.getUserData();
    const userToken = this.getUserToken();
    if (!userData?.email || !userToken) {
      return '';
    }

    const decodedToken = this.decodeJwtPayload(userToken);
    if (!decodedToken?.exp) {
      this.dbg('JWT payload decode failed, keeping current token');
      return userToken;
    }

    return userToken;
  }

  decodeJwtPayload(token: string): any | null {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length < 2) {
        return null;
      }

      const payload = tokenParts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      const normalizedPayload = payload.padEnd(Math.ceil(payload.length / 4) * 4, '=');
      return JSON.parse(window.atob(normalizedPayload));
    } catch (error) {
      this.dbg('decodeJwtPayload error', error);
      return null;
    }
  }

  isJwtTokenNearExpiry(token: string, refreshBufferSeconds: number = 120): boolean {
    if (!token) {
      return false;
    }

    const decodedToken = this.decodeJwtPayload(token);
    const currentDateTime = Math.floor(Date.now() / 1000);

    if (!decodedToken?.exp) {
      this.dbg('JWT payload decode failed while checking expiry');
      return false;
    }

    return currentDateTime >= decodedToken.exp - refreshBufferSeconds;
  }

  isJwtTokenExpired(token: string): boolean {
    if (!token) {
      return true;
    }

    const decodedToken = this.decodeJwtPayload(token);
    const currentDateTime = Math.floor(Date.now() / 1000);

    if (!decodedToken?.exp) {
      this.dbg('JWT payload decode failed while checking expired state');
      return true;
    }

    return currentDateTime >= decodedToken.exp;
  }

  clearSession() {
    this.clearUserData();
    this.clearUserToken();
  }

  generateFormToken(): { token: string; expireAt: number } {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    const token = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
    const expireAt = Date.now() + 10 * 60 * 1000; // 10 min from now in ms

    return { token, expireAt };
  }
  
  dbg(param1:any,param2:any=""){
    const isProd = environment.production;
    const stack = new Error().stack;
    const callerInfo = this.getCallerInfo(stack);

    if(!isProd){
      console.log(`Called from: ${callerInfo}`, param1, param2);
    }
  }

  private getCallerInfo(stack: string | undefined): string {
    if (!stack) {
      return 'Stack trace unavailable';
    }

    // Split the stack trace into lines
    const stackLines = stack.split('\n');

    // Find the first stack line that refers to the caller (skip the first line)
    for (let i = 2; i < stackLines.length; i++) {
      const line = stackLines[i].trim();
      
      // Extract the relevant part of the stack line (file and line number)
      const match = line.match(/at\s+(.*)/);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return 'Caller information unavailable';
  }

  // ===== EXTRA UTILITY FUNCTIONS =====

  /**
   * Format error message from API response
   * @param error Error object from API
   * @returns Formatted error message string
   */
  getErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.error) {
      // Handle validation errors from API
      if (typeof error.error === 'object') {
        const firstKey = Object.keys(error.error)[0];
        return error.error[firstKey] || 'An error occurred';
      }
      return error.error.message || error.error;
    }

    if (error?.message) {
      return error.message;
    }

    return 'An unexpected error occurred';
  }

  /**
   * Check if object is empty
   * @param obj Object to check
   * @returns true if empty, false otherwise
   */
  isEmpty(obj: any): boolean {
    if (obj === null || obj === undefined) return true;
    if (Array.isArray(obj)) return obj.length === 0;
    if (typeof obj === 'object') return Object.keys(obj).length === 0;
    if (typeof obj === 'string') return obj.trim() === '';
    return false;
  }

  /**
   * Capitalize first letter of string
   * @param str String to capitalize
   * @returns Capitalized string
   */
  capitalize(str: string): string {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  /**
   * Generate unique ID
   * @returns Unique string ID
   */
  generateUniqueId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Deep clone object
   * @param obj Object to clone
   * @returns Deep cloned object
   */
  deepClone(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Sleep/delay execution
   * @param ms Milliseconds to sleep
   * @returns Promise that resolves after delay
   */
  sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get query parameter from URL
   * @param key Parameter key to retrieve
   * @returns Parameter value or null
   */
  getQueryParam(key: string): string | null {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(key);
    } catch {
      return null;
    }
  }

  /**
   * Copy text to clipboard
   * @param text Text to copy
   * @returns Promise<boolean> Success status
   */
  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      await this.show_message('Copied to clipboard', 'success');
      return true;
    } catch (err) {
      await this.show_message('Failed to copy to clipboard', 'error');
      return false;
    }
  }

  /**
   * Truncate string with ellipsis
   * @param str String to truncate
   * @param length Max length (default: 50)
   * @returns Truncated string
   */
  truncate(str: string, length: number = 50): string {
    if (!str) return '';
    return str.length > length ? str.substring(0, length) + '...' : str;
  }

  /**
   * Validate email address
   * @param email Email to validate
   * @returns true if valid email
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number (basic validation)
   * @param phone Phone number to validate
   * @returns true if valid phone
   */
  isValidPhone(phone: string): boolean {
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
  }

  /**
   * Check if URL is valid
   * @param url URL to validate
   * @returns true if valid URL
   */
  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Format date to readable string
   * @param date Date object or ISO string
   * @returns Formatted date string
   */
  formatDate(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Convert UTC SQL datetime string to Local readable string
   */
  formatUtcToLocal(utcDateStr: string): string {
    if (!utcDateStr) return '';
    // Append 'Z' to force UTC parsing
    const date = new Date(utcDateStr.replace(' ', 'T') + 'Z');
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Format date to date only (no time)
   * @param date Date object or ISO string
   * @returns Formatted date string (MM/DD/YYYY)
   */
  formatDateOnly(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US');
  }

  /**
   * Format date to time only (no date)
   * @param date Date object or ISO string
   * @returns Formatted time string (HH:MM AM/PM)
   */
  formatTimeOnly(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  /*
  * Convert Date object to UTC SQL datetime string (YYYY-MM-DD HH:MM:SS)
  */
  toUtcSqlDateTime(date: Date) {
    return date.toISOString().slice(0, 19).replace('T', ' ');
  }

  toSqlDateTime(date: Date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const seconds = `${date.getSeconds()}`.padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Convert seconds to human readable time (e.g., "2 hours ago")
   * @param seconds Seconds elapsed
   * @returns Human readable time string
   */
  getTimeAgo(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;

    return 'a month ago';
  }

  /**
   * Debounce function - delays execution
   * @param func Function to debounce
   * @param wait Wait time in milliseconds
   * @returns Debounced function
   */
  debounce(func: Function, wait: number) {
    let timeout: any;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * Check if object has property
   * @param obj Object to check
   * @param prop Property to look for
   * @returns true if property exists
   */
  hasProperty(obj: any, prop: string): boolean {
    return obj && Object.prototype.hasOwnProperty.call(obj, prop);
  }

  /**
   * Merge two objects (shallow merge)
   * @param obj1 First object
   * @param obj2 Second object
   * @returns Merged object
   */
  mergeObjects(obj1: any, obj2: any): any {
    return { ...obj1, ...obj2 };
  }

  /**
   * Get random item from array
   * @param arr Array to select from
   * @returns Random item from array
   */
  getRandomItem(arr: any[]): any {
    if (!arr || arr.length === 0) return null;
    return arr[Math.floor(Math.random() * arr.length)];
  }

  /**
   * Shuffle array (Fisher-Yates algorithm)
   * @param arr Array to shuffle
   * @returns Shuffled array
   */
  shuffleArray(arr: any[]): any[] {
    const shuffled = [...arr];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /**
   * Remove duplicates from array
   * @param arr Array to deduplicate
   * @param key Optional key for objects (e.g., 'id')
   * @returns Array without duplicates
   */
  removeDuplicates(arr: any[], key?: string): any[] {
    if (!key) {
      return [...new Set(arr)];
    }
    return arr.filter((obj, index, self) =>
      index === self.findIndex(t => t[key] === obj[key])
    );
  }

  /**
   * Group array by property
   * @param arr Array to group
   * @param key Property to group by
   * @returns Grouped object
   */
  groupBy(arr: any[], key: string): any {
    return arr.reduce((grouped, item) => {
      const group = item[key];
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(item);
      return grouped;
    }, {});
  }

  /**
   * Filter array by search term
   * @param arr Array to filter
   * @param term Search term
   * @param keys Keys to search in
   * @returns Filtered array
   */
  searchArray(arr: any[], term: string, keys: string[]): any[] {
    const lowerTerm = term.toLowerCase();
    return arr.filter(item =>
      keys.some(key =>
        String(item[key]).toLowerCase().includes(lowerTerm)
      )
    );
  }

  /**
   * Sort array by property
   * @param arr Array to sort
   * @param key Property to sort by
   * @param order 'asc' or 'desc'
   * @returns Sorted array
   */
  sortBy(arr: any[], key: string, order: 'asc' | 'desc' = 'asc'): any[] {
    const sorted = [...arr];
    sorted.sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }

  /**
   * Convert camelCase to kebab-case
   * @param str String to convert
   * @returns Kebab-case string
   */
  camelToKebab(str: string): string {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
  }

  /**
   * Convert kebab-case to camelCase
   * @param str String to convert
   * @returns camelCase string
   */
  kebabToCamel(str: string): string {
    return str.replace(/-./g, x => x[1].toUpperCase());
  }

  /**
   * Convert snake_case to camelCase
   * @param str String to convert
   * @returns camelCase string
   */
  snakeToCamel(str: string): string {
    return str.replace(/_./g, x => x[1].toUpperCase());
  }

  /**
   * Check if value is null or undefined
   * @param value Value to check
   * @returns true if null or undefined
   */
  isNullOrUndefined(value: any): boolean {
    return value === null || value === undefined;
  }

  /**
   * Get safe value with fallback
   * @param value Value to check
   * @param fallback Fallback value
   * @returns Value or fallback
   */
  safeValue(value: any, fallback: any = ''): any {
    return this.isNullOrUndefined(value) ? fallback : value;
  }

  /**
   * Parse JSON safely
   * @param jsonStr JSON string to parse
   * @param fallback Fallback value if parse fails
   * @returns Parsed object or fallback
   */
  safeJsonParse(jsonStr: string, fallback: any = null): any {
    try {
      return JSON.parse(jsonStr);
    } catch {
      return fallback;
    }
  }

  /**
   * Stringify object safely
   * @param obj Object to stringify
   * @param fallback Fallback string if stringify fails
   * @returns JSON string or fallback
   */
  safeJsonStringify(obj: any, fallback: string = ''): string {
    try {
      return JSON.stringify(obj);
    } catch {
      return fallback;
    }
  }

  /**
   * Calculate percentage
   * @param current Current value
   * @param total Total value
   * @returns Percentage value
   */
  calculatePercentage(current: number, total: number): number {
    if (total === 0) return 0;
    return Math.round((current / total) * 100);
  }

  /**
   * Round number to decimal places
   * @param num Number to round
   * @param decimals Number of decimal places
   * @returns Rounded number
   */
  roundToDecimal(num: number, decimals: number = 2): number {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }

  /**
   * Format number as currency
   * @param num Number to format
   * @param currency Currency code (default: USD)
   * @returns Formatted currency string
   */
  formatCurrency(num: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(num);
  }

  /**
   * Format number with thousand separator
   * @param num Number to format
   * @returns Formatted number string
   */
  formatNumber(num: number): string {
    return new Intl.NumberFormat('en-US').format(num);
  }

  /**
   * Check if string contains special characters
   * @param str String to check
   * @returns true if contains special characters
   */
  hasSpecialCharacters(str: string): boolean {
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(str);
  }

  /**
   * Remove special characters from string
   * @param str String to clean
   * @returns String without special characters
   */
  removeSpecialCharacters(str: string): string {
    return str.replace(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g, '');
  }

  /**
   * Highlight search term in text
   * @param text Text to highlight in
   * @param term Term to highlight
   * @param highlightClass CSS class for highlighting
   * @returns HTML string with highlighted term
   */
  highlightText(text: string, term: string, highlightClass: string = 'highlight'): string {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, 'gi');
    return text.replace(regex, `<span class="${highlightClass}">$1</span>`);
  }

  /**
   * Get value from nested object using path
   * @param obj Object to get value from
   * @param path Path to property (e.g., 'user.profile.name')
   * @param fallback Fallback value
   * @returns Value from path or fallback
   */
  getNestedValue(obj: any, path: string, fallback: any = null): any {
    const keys = path.split('.');
    let value = obj;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return fallback;
      }
    }

    return value;
  }

  /**
   * Set value in nested object using path
   * @param obj Object to set value in
   * @param path Path to property (e.g., 'user.profile.name')
   * @param value Value to set
   */
  setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }

  formatMessage(msg: string) {
    return msg.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>');
  }
}
